import React, { ReactNode } from "react";
import {
  Box,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { ProjectRecord, isApiErrorResponse, getApiErrorMessage } from "@/types";
import Card from "@/components/Card";
import { StyledBadge, StyledIconButton } from "@/components/styled";
import { getFileStorageBadgeVariant } from "@/utils/getIncentiveBadgeVariant.ts";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter.ts";
import { getEnrollmentBadgeVariant } from "@/utils/getEnrollmentBadgeVariant.ts";
import { getSizingBadgeVariant } from "@/utils/getSizingBadgeVariant.ts";
import { useProjectsStore } from "@/store/projectsStore.ts";
import { formatCurrency } from "@/utils/formatCurrency.ts";
import EditProjectModal from "@/pages/ProjectDetailsPage/components/EditProjectModal.tsx";
import BadgeSelect from "@/pages/admin-pages/UserManagementPage/components/BadgeSelect.tsx";
import { replaceUnderscoresWithSpaces } from "@/utils/replaceUnderscoreWithSpaces.ts";
import { useProjectDocument } from "@/hooks/useProjectDocument.ts";
import { calculateTotalUsableCapacity } from "@/utils/calculateTotalNominalPower";
import { useAuthStore } from "@/store/authStore.ts";
import { allowEditProject } from "@/utils/allowEditProject.ts";
import { calculateUsableBatteryCapacity } from "@/utils/calculateUsableBatteryCapacity.ts";
import dayjs from "dayjs";
import { useState, useCallback, useEffect } from "react";
import Modal from "@/components/Modal";
import ConfirmationModal from "@/components/ConfirmationModal";
import {
  analyzeSizingToolReq,
  uploadProjectFileReq,
  uploadProjectDocumentReq,
  getProjectReq,
} from "@/api/projectsApi";
import { useForm } from "react-hook-form";
import { getBatteryOemsReq } from "@/api/userApi";
import { getBatteryListReq } from "@/api/batteryApi";
import SizingToolForm from "./ProjectInfoCard/components/SizingToolForm";
import { SizingToolResultModal } from "./ProjectInfoCard/components/SizingToolResultModal";
import { useGlobalSizingToolStore } from "@/store/globalSizingToolStore";
import ConfirmProjectSignatureModal from "@/components/ConfirmProjectSignatureModal";
import { parseStreetAddress } from "@/utils/addressUtils";
import { passSizingToolReq, setSizingStatusReq } from "@/api/adminApi";
import ConfirmationModals from "./ConfirmationModals";
import IncentiveStatusSelect from "@/pages/ProjectsPage/components/IncentiveStatusSelect";
import RejectedProjectModal from "@/pages/ProjectsPage/components/RejectedProjectModal";
import CorrectionNeededModal from "@/pages/ProjectsPage/components/CorrectionNeededModal";
import { useProjectStatusUpdate } from "@/hooks/useProjectStatusUpdate";
import EditIncentiveModal from "@/components/EditIncentiveModal";
import CreateAddOnProjectButton from "@/components/CreateAddOnProjectButton";
import { Link as RouterLink } from "react-router-dom";
import RecalculateIncentiveModal from "@/components/RecalculateIncentiveModal";
import EditOperationalizedDateModal from "@/components/EditOperationalizedDateModal";
import EditEstimatedInstallationDateModal from "@/components/EditEstimatedInstallationDateModal";
import { formatOperationalizedDate } from "@/utils/formatDate";

const FILE_UPLOAD_ERROR_MESSAGE = "File upload failed. Please try again.";

export const BoxInfoWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "40px",
  flexWrap: "wrap",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
  },
}));

const ProjectInfoItem = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <Typography
        variant={"body1Medium"}
        sx={{ color: "textLightGray3", whiteSpace: "nowrap" }}
      >
        {title}
      </Typography>
      {typeof children === "string" ? (
        <Typography
          variant={"body1Medium"}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
          }}
        >
          {children}
        </Typography>
      ) : (
        children
      )}
    </Box>
  );
};

const ProjectInfoCard = ({
  getFilesReq,
  projectId,
}: {
  getFilesReq: () => void;
  projectId?: number;
}) => {
  const projectDetails = useProjectsStore((state) => state.projectDetails);
  const getProjectDetails = useProjectsStore(
    (state) => state.getProjectDetails
  );
  const role = useAuthStore((state) => state.role);
  const openGlobalSizingTool = useGlobalSizingToolStore(
    (state) => state.openModal
  );

  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const { handleDownloadDocument } = useProjectDocument();

  const isAdmin = role === "administrator";
  const isContractor = role === "contractor";

  const isProjectLoaded =
    projectDetails && Object.keys(projectDetails).length > 0;

  const projectExtensions = projectDetails?.ProjectExtensions ?? [];
  const isShowExtendProjectDueDate =
    (isAdmin || isContractor) && projectExtensions?.length > 0;

  const [openSizingTool, setOpenSizingTool] = useState(false);
  const [showTerminationConfirmation, setShowTerminationConfirmation] =
    useState(false);

  const [sizingForm, setSizingForm] = useState<any>({
    accountNumber: projectDetails?.accountHash || "",
    zipCode: projectDetails?.zipCode || "",
    existingSolarKwac: projectDetails?.solarKwac || "",
    newSolarKwac: projectDetails?.newSolarKwac || "",
    batteryManufacturer: "",
    batteryManufacturerId: "",
    batteryProductName: "",
    batteryQuantity: "",
    nominalPower: String(
      projectDetails?.nominalPowerkw || projectDetails?.ptoNameplateKw || "0"
    ),
    usableCapacity: projectDetails?.usableCapacity || "",
    totalBatteryUsableCapacity: calculateTotalUsableCapacity(
      projectDetails?.usableCapacity || "",
      projectDetails?.numberOfBatteries?.toString() || ""
    ),
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [sizingResult, setSizingResult] = useState(null);
  const [sizingModalType, setSizingModalType] = useState<
    | "form"
    | "success"
    | "insufficient"
    | "genericError"
    | "insufficientData"
    | "accountNotFound"
    | "calculationFailure"
  >("form");
  const [sizingError, setSizingError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [showSignatureConfirmation, setShowSignatureConfirmation] =
    useState(false);
  const [showPassSizingConfirmation, setShowPassSizingConfirmation] =
    useState(false);
  const [showRejectSizingConfirmation, setShowRejectSizingConfirmation] =
    useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const setProjectDetails = useProjectsStore(
    (state) => state.setProjectsDetails
  );

  // Use shared hook for status update logic
  const {
    rejectProjectId,
    correctionNeededProjectId,
    updateEnrolmentStatus,
    refreshProjectDetails,
    setRejectProjectId,
    setCorrectionNeededProjectId,
    enrollmentItems,
    isEnrollmentReadOnly,
  } = useProjectStatusUpdate(projectDetails);
  const getProjectFiles = useProjectsStore((state) => state.getProjectFiles);

  const [sizingPassed, setSizingPassed] = useState(
    (projectDetails as { sizing_passed?: number })?.sizing_passed || 0
  );
  const [sizingSubmitted, setSizingSubmitted] = useState(
    (projectDetails as { sizing_submitted?: number })?.sizing_submitted || 0
  );

  const hasSizingToolBeenPassed = Boolean(projectDetails?.sizing_passed);
  // SDCP-1325: sizing now has a real Failed state alongside Passed and Pending.
  const sizingStatus = projectDetails?.sizing_status;
  const hasSizingToolBeenRejected = sizingStatus === "failed";
  const hasSizingVerdict = hasSizingToolBeenPassed ||
    hasSizingToolBeenRejected ||
    (projectDetails?.sizing_passed === 0 &&
      Boolean(projectDetails?.sizing_submitted));

  const [validatedAccountNumber, setValidatedAccountNumber] =
    useState<string>("");
  const [validatedCustomerName, setValidatedCustomerName] =
    useState<string>("");

  const getBatteryOems = useCallback(
    (searchValue: string) => getBatteryOemsReq(searchValue),
    []
  );

  const getBatteryList = useCallback(
    async (searchValue: string) => {
      try {
        const batteryManufacturerId = sizingForm.batteryManufacturerId;

        if (!batteryManufacturerId) {
          return { data: { batteryModels: [] } };
        }

        const response = await getBatteryListReq(
          String(batteryManufacturerId),
          searchValue
        );
        return response;
      } catch (error) {
        return { data: { batteryModels: [] } };
      }
    },
    [sizingForm.batteryManufacturerId]
  );

  const handleConfirm = useCallback(() => {
    if (projectDetails?.id) {
      getProjectDetails(projectDetails.id);
      setSuccessMessage(
        `Project #${projectDetails.id} has been successfully sent for signature`
      );
    }
  }, [projectDetails?.id, getProjectDetails]);

  const handleSizingInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSizingForm({ ...sizingForm, [e.target.name]: e.target.value });
  };

  const handleAnalyze = async (formValues?: any) => {
    setAnalyzing(true);
    try {
      const values = formValues || sizingForm;

      const payload = {
        accountNumber: values.accountNumber,
        existingSolarSystemSize: Number(values.existingSolarKwdc) || 0,
        newSolarSystemSize: Number(values.newSolarKwdc) || 0,
        totalBatteryUsableCapacity:
          Number(values.totalBatteryUsableCapacity) || 0,
        numberOfBatteries: Number(values.batteryQuantity) || 1,
        zipCode: values.zipCode,
        solar_installation_duration_less_than_12_months:
          values.existingSolarLessThan12Months === "yes",
        existing_battery_installed: values.hasExistingBattery || false,
      };

      const res = await analyzeSizingToolReq(payload);

      if (res.data.success) {
        const dataValues = res.data.dataValues || {};
        const customerFirstName = dataValues.Customer_First_Name || "";
        const customerLastName = dataValues.Customer_Last_Name || "";
        let customerName = `${customerFirstName} ${customerLastName}`.trim();
        if (!customerName) {
          customerName = (values?.customerName as string) || "";
        }

        setValidatedAccountNumber(values.accountNumber);
        setValidatedCustomerName(customerName);

        if (res.data.isSufficient !== undefined) {
          setSizingResult(res.data);
          const newModalType = res.data.isSufficient
            ? "success"
            : "insufficient";
          setSizingModalType(newModalType);

          if (projectDetails?.id) {
            await getProjectDetails(projectDetails.id);
          }
        } else if (res.data.sufficient !== undefined) {
          if (res.data.sufficient) {
            setSizingResult(res.data);
            setSizingModalType("success");
          } else {
            setSizingResult(res.data);
            setSizingModalType("insufficient");
          }

          if (projectDetails?.id) {
            await getProjectDetails(projectDetails.id);
          }
        }
        const actualSizingPassed = res.data.isSufficient ? 1 : 0;
        const actualSizingSubmitted =
          res.data.sizing_submitted || actualSizingPassed;

        setSizingPassed(actualSizingPassed);
        setSizingSubmitted(actualSizingSubmitted);

        setProjectDetails({
          ...projectDetails,
          sizing_passed: actualSizingPassed,
          sizing_submitted: actualSizingSubmitted,
        });

        if (
          res.data.isSufficient &&
          projectDetails?.id &&
          !Boolean(res.data.sizing_passed)
        ) {
          let attempts = 0;
          const maxAttempts = 5;

          const checkSizingStatus = async () => {
            attempts++;

            try {
              const response = await getProjectReq(projectDetails.id);
              const project = response.data;

              setProjectDetails(project);
              setSizingPassed(project.sizing_passed || 0);
              setSizingSubmitted(project.sizing_submitted || 0);

              if (
                project.sizing_passed !== undefined &&
                project.sizing_passed !== null
              ) {
                return;
              }

              if (attempts >= maxAttempts) {
                return;
              }

              setTimeout(checkSizingStatus, 4000);
            } catch (error) {
              if (attempts < maxAttempts) {
                setTimeout(checkSizingStatus, 4000);
              }
            }
          };

          setTimeout(checkSizingStatus, 4000);
        } else {
        }

        setAnalyzing(false);
      } else {
        const code = res.data.code;
        if (code === "INVALID_ACCOUNT") setSizingModalType("accountNotFound");
        else if (code === "INSUFFICIENT_DATA")
          setSizingModalType("insufficientData");
        else if (code === "NUMBER_ISSUE") setSizingModalType("genericError");
        else if (code === "CALCULATION_FAILURE")
          setSizingModalType("calculationFailure");
        else setSizingModalType("genericError");

        setSizingError(res.data.error || res.data.message || "Unknown error");
        setShowUpload(true);
        setAnalyzing(false);
      }
    } catch (e) {
      if (isApiErrorResponse(e)) {
        const code = e.response?.data?.code;
        if (code === "INVALID_ACCOUNT") setSizingModalType("accountNotFound");
        else if (code === "INSUFFICIENT_DATA")
          setSizingModalType("insufficientData");
        else if (code === "NUMBER_ISSUE") setSizingModalType("genericError");
        else if (code === "CALCULATION_FAILURE")
          setSizingModalType("calculationFailure");
        else setSizingModalType("genericError");
        setSizingError(e.response?.data?.error || "Unknown error");
      } else {
        setSizingModalType("genericError");
        setSizingError("Unknown error");
      }
      setShowUpload(true);
      setAnalyzing(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) setFile(e.target.files[0]);
  };

  const handleUpload = async (uploadFile?: File) => {
    const fileToUpload = uploadFile || file;
    if (!fileToUpload) {
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", fileToUpload);
      formData.append("type", "system sizing tool");

      const response = await uploadProjectDocumentReq(
        projectDetails.id,
        formData
      );

      setSizingSubmitted(1);
      setUploadSuccess(true);
      setShowUpload(false);
      setOpenSizingTool(false);

      if (
        projectDetails?.id &&
        sizingResult &&
        (sizingResult as any)?.isSufficient
      ) {
        let attempts = 0;
        const maxAttempts = 5;

        const checkSizingStatus = async () => {
          attempts++;
          try {
            const response = await getProjectReq(projectDetails.id);
            const project = response.data;
            setProjectDetails(project);
            setSizingPassed(project.sizing_passed || 0);
            setSizingSubmitted(project.sizing_submitted || 0);

            // Stop polling if sizing_passed is true/false (any definitive result) OR max attempts reached
            if (
              project.sizing_passed !== undefined &&
              project.sizing_passed !== null
            ) {
              return; // Stop when we get any definitive result (0 or 1)
            }

            if (attempts >= maxAttempts) {
              return;
            }

            setTimeout(checkSizingStatus, 4000);
          } catch (error) {
            if (attempts < maxAttempts) {
              setTimeout(checkSizingStatus, 4000);
            }
          }
        };

        setTimeout(checkSizingStatus, 4000);
      }

      setProjectDetails({ ...projectDetails });
    } catch (e) {
      const errorMsg = getApiErrorMessage(e, FILE_UPLOAD_ERROR_MESSAGE);
      setSizingError(errorMsg);
    }
    setUploading(false);
  };

  const uploadDocumentHandler = async (uploadFile?: File) => {
    const fileToUpload = uploadFile || file;
    if (!fileToUpload) {
      setSizingError("Please select a file before uploading.");
      return;
    }

    setUploading(true);
    setSizingError(null);

    try {
      const formData = new FormData();
      formData.append("file", fileToUpload);
      formData.append("type", "system sizing tool");

      await uploadProjectFileReq(projectDetails.id, formData);

      setSizingSubmitted(1);
      setUploadSuccess(true);
      setShowUpload(false);
      setOpenSizingTool(false);
      setSizingModalType("form");
      setSizingResult(null);
      setSizingError(null);

      if (projectDetails?.id) {
        const projectResponse = await getProjectReq(projectDetails.id);
        setProjectDetails(projectResponse.data);
        await getProjectFiles(projectDetails.id, 1, 10, "", [], null);
      }
    } catch (e) {
      console.error("❌ Upload failed:", e);
      const errorMsg = getApiErrorMessage(e, FILE_UPLOAD_ERROR_MESSAGE);
      setErrorMessage(errorMsg);
      setSizingError(errorMsg);
    }

    setUploading(false);
  };

  const handleModalUpload = async () => {
    const fileToUpload = file;
    if (!fileToUpload) {
      setSizingError("Please select a file before uploading.");
      throw new Error("No file selected");
    }

    setUploading(true);
    setSizingError(null);

    try {
      const formData = new FormData();
      formData.append("file", fileToUpload);
      formData.append("type", "system sizing tool");

      await uploadProjectFileReq(projectDetails.id, formData);

      setOpenSizingTool(false);
      setSizingModalType("form");
      setSizingResult(null);
      setSizingError(null);
      getProjectFiles(projectDetails.id, 1, 10, "", [], null);
    } catch (e) {
      const errorMsg = getApiErrorMessage(e, FILE_UPLOAD_ERROR_MESSAGE);
      setErrorMessage(errorMsg);
      setSizingError(errorMsg);
      throw e;
    } finally {
      setUploading(false);
    }
  };

  const hasInsufficientSizing =
    projectDetails?.sizing_passed === 0 &&
    projectDetails?.sizing_submitted === 1;

  const [isPassingSizingTool, setIsPassingSizingTool] = useState(false);

  const handlePassSizingTool = async () => {
    if (!projectDetails?.id || isPassingSizingTool) return;

    try {
      setIsPassingSizingTool(true);
      await passSizingToolReq(projectDetails.id);
      await getProjectDetails(projectDetails.id);
      setSuccessMessage(
        `Project #${projectDetails.id} has been successfully passed for sizing tool`
      );
    } catch (error) {
      console.error("Failed to pass sizing tool:", error);
      setErrorMessage("Failed to pass sizing tool. Please try again.");
    } finally {
      setIsPassingSizingTool(false);
    }
  };

  const [isRejectingSizingTool, setIsRejectingSizingTool] = useState(false);

  const handleRejectSizingTool = async () => {
    if (!projectDetails?.id || isRejectingSizingTool) return;

    try {
      setIsRejectingSizingTool(true);
      await setSizingStatusReq(projectDetails.id, "failed");
      await getProjectDetails(projectDetails.id);
      setSuccessMessage(
        `Project #${projectDetails.id} has been rejected for sizing tool`
      );
    } catch (error) {
      console.error("Failed to reject sizing tool:", error);
      setErrorMessage("Failed to reject sizing tool. Please try again.");
    } finally {
      setIsRejectingSizingTool(false);
    }
  };

  const handleConfirmSignature = () => {
    setShowSignatureConfirmation(false);
    setShowSignatureModal(true);
  };

  const handleConfirmPassSizing = () => {
    setShowPassSizingConfirmation(false);
    handlePassSizingTool();
  };

  const handleConfirmRejectSizing = () => {
    setShowRejectSizingConfirmation(false);
    handleRejectSizingTool();
  };

  const handleCloseSuccessSnackbar = () => {
    setSuccessMessage("");
  };

  const handleCloseErrorSnackbar = () => {
    setErrorMessage("");
  };

  const handleCloseRequest = () => {
    if (openSizingTool && analyzing) {
      setShowTerminationConfirmation(true);
    } else {
      setOpenSizingTool(false);
      setSizingError(null);
      setSizingResult(null);
      setShowUpload(false);
      setUploadSuccess(false);
      setSizingModalType("form");
    }
  };

  const handleTerminationConfirm = () => {
    setAnalyzing(false);
    setOpenSizingTool(false);
    setSizingError(null);
    setSizingResult(null);
    setShowUpload(false);
    setUploadSuccess(false);
    setSizingModalType("form");
    setShowTerminationConfirmation(false);
  };

  const handleTerminationCancel = () => {
    setShowTerminationConfirmation(false);
  };

  // SDCP-1340: add-ons are created from the original project, never from an add-on or a
  // cancelled project.
  const canCreateAddOn =
    isAdmin &&
    isProjectLoaded &&
    !projectDetails?.parentProjectId &&
    projectDetails?.incentiveStatus !== "cancelled";

  return (
    <Box>
      {canCreateAddOn && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <CreateAddOnProjectButton
            projectId={projectDetails.id}
            onError={setErrorMessage}
          />
        </Box>
      )}
      {isProjectLoaded && !hasSizingToolBeenPassed && (
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 2 }}
        >
          <Button
            variant="contained"
            onClick={() => {
              openGlobalSizingTool(projectDetails, false, false);
            }}
            sx={{
              width: 200,
              height: 48,
              border: "none",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "16px",
              color: "#fff",
              background: "#0076CE",
              boxShadow: "none",
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                background: "#0056a3",
                boxShadow: "none",
              },
            }}
          >
            System Sizing Tool
          </Button>
        </Box>
      )}

      <Card sx={{ position: "relative" }}>
        <Box
          sx={{ display: "flex", gap: "12px", justifyContent: "space-between" }}
        >
          <BoxInfoWrapper sx={{ overflow: "hidden" }}>
            <ProjectInfoItem title={"ID"}>
              {projectDetails?.id?.toString()?.padStart(3, "0")}
            </ProjectInfoItem>
            <ProjectInfoItem title={"Customer name"}>
              {projectDetails?.customerName}
            </ProjectInfoItem>
            <ProjectInfoItem title={"Address"}>
              {parseStreetAddress(projectDetails?.address || "")}
            </ProjectInfoItem>
            <ProjectInfoItem title={"City"}>
              {projectDetails?.city}
            </ProjectInfoItem>
            <ProjectInfoItem title={"Zip code"}>
              {projectDetails?.zipCode}
            </ProjectInfoItem>
            <ProjectInfoItem title={"Estimated Installation Date"}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <span>
                  {projectDetails?.estimated_installation_date
                    ? dayjs(projectDetails.estimated_installation_date).format("MMMM D, YYYY")
                    : "—"}
                </span>
                {projectDetails?.id && (
                  <EditEstimatedInstallationDateModal
                    projectId={projectDetails.id}
                    currentDate={projectDetails.estimated_installation_date || null}
                    onSuccess={() => {
                      if (projectDetails?.id) {
                        getProjectDetails(projectDetails.id);
                      }
                    }}
                  />
                )}
              </Box>
            </ProjectInfoItem>
            <ProjectInfoItem title={"Account #"}>
              {projectDetails?.accountHash}
            </ProjectInfoItem>
            {projectDetails?.parentProject && (
              <ProjectInfoItem title={"Add-on of"}>
                <RouterLink to={`/projects/${projectDetails.parentProject.id}`}>
                  Project #{projectDetails.parentProject.id}
                </RouterLink>
              </ProjectInfoItem>
            )}
            {!!projectDetails?.addOnProjects?.length && (
              <ProjectInfoItem title={"Add-on projects"}>
                {projectDetails.addOnProjects.map((addOn, index) => (
                  <span key={addOn.id}>
                    {index > 0 && ", "}
                    <RouterLink to={`/projects/${addOn.id}`}>
                      Project #{addOn.id}
                    </RouterLink>
                  </span>
                ))}
              </ProjectInfoItem>
            )}
            <ProjectInfoItem title={"Customer email"}>
              {projectDetails?.accountEmail}
            </ProjectInfoItem>
            <ProjectInfoItem title={"Existing inverter"}>
              {projectDetails?.existingInverter}
            </ProjectInfoItem>
            <ProjectInfoItem title={"Existing Solar - kW-DC"}>
              {projectDetails?.solarKwdc}
            </ProjectInfoItem>
            <ProjectInfoItem title={"Existing Solar - kW-AC"}>
              {projectDetails?.solarKwac}
            </ProjectInfoItem>
            <ProjectInfoItem title={"Existing Solar - Annual Production"}>
              {projectDetails?.solarAnnualProduction}
            </ProjectInfoItem>
            <ProjectInfoItem title={"New Solar - kW-DC"}>
              {projectDetails?.newSolarKwdc}
            </ProjectInfoItem>
            <ProjectInfoItem title={"New Solar - kW-AC"}>
              {projectDetails?.newSolarKwac}
            </ProjectInfoItem>
            <ProjectInfoItem title={"New Solar - Annual Production"}>
              {projectDetails?.newSolarAnnualProduction}
            </ProjectInfoItem>
            <ProjectInfoItem title={"Contractor"}>
              {projectDetails?.contractor}
            </ProjectInfoItem>
            <ProjectInfoItem title={"Battery Manufacturer"}>
              {projectDetails?.batteryOem}
            </ProjectInfoItem>
            <ProjectInfoItem title={"Battery Product"}>
              {projectDetails?.batteryModel}
            </ProjectInfoItem>
            <ProjectInfoItem title={"Battery Capacity (kWh)"}>
              {calculateUsableBatteryCapacity(
                projectDetails?.usableCapacity,
                projectDetails?.numberOfBatteries
              )}
            </ProjectInfoItem>

            <ProjectInfoItem title={"Participation"}>
              {projectDetails?.participation === "weekdays_weekends"
                ? "Weekdays & weekends"
                : `${capitalizeFirstLetter(
                    projectDetails?.participation ?? ""
                  )} Only`}
            </ProjectInfoItem>
            <ProjectInfoItem title={"Upfront Incentive Payment"}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant={"body1Medium"}>
                  {formatCurrency(
                    Number(projectDetails?.upfrontIncentivePayment) / 100
                  )}
                </Typography>
                {/* SDCP-1305: the only two ways to change a stored incentive — a deliberate
                    recalculation, or a manual correction. Project edits leave it alone. */}
                {isAdmin && Boolean(projectDetails?.id) && (
                  <>
                    <EditIncentiveModal
                      iconOnly
                      projectId={projectDetails.id}
                      currentIncentive={
                        Number(projectDetails?.upfrontIncentivePayment) || 0
                      }
                      onSuccess={() => {
                        getProjectDetails(projectDetails.id);
                        setSuccessMessage(
                          `Incentive amount updated successfully for Project #${projectDetails.id}`
                        );
                      }}
                    />
                    <RecalculateIncentiveModal
                      projectId={projectDetails.id}
                      currentIncentive={
                        Number(projectDetails?.upfrontIncentivePayment) || 0
                      }
                      onSuccess={() => {
                        getProjectDetails(projectDetails.id);
                        setSuccessMessage(
                          `Incentive amount recalculated successfully for Project #${projectDetails.id}`
                        );
                      }}
                    />
                  </>
                )}
              </Box>
            </ProjectInfoItem>
            {projectDetails?.projectSignature && (
              <ProjectInfoItem title={"Participant enrollment"}>
                <Typography
                  variant={"body1Medium"}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                    cursor: "pointer",
                    color: "#0076CE",
                    textDecoration: "underline",
                    "&:hover": {
                      color: "#0056a3",
                    },
                  }}
                  onClick={async () => {
                    if (projectDetails) {
                      try {
                        await handleDownloadDocument(projectDetails);
                      } catch (error) {
                        console.error("Failed to download document:", error);
                      }
                    }
                  }}
                >
                  {projectDetails?.projectSignature?.fileName?.length > 20
                    ? `${projectDetails?.projectSignature?.fileName.substring(
                        0,
                        20
                      )}...`
                    : projectDetails?.projectSignature?.fileName}
                </Typography>
              </ProjectInfoItem>
            )}
            {isShowExtendProjectDueDate && (
              <ProjectInfoItem title="Extend To Date">
                {dayjs(
                  projectExtensions[projectExtensions.length - 1]?.extendToDate
                ).format("MMMM D, YYYY h:mm a")}
              </ProjectInfoItem>
            )}
          </BoxInfoWrapper>
          <Box
            sx={{
              ...(downSm && {
                position: "absolute",
                top: "20px",
                right: "16px",
              }),
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {projectDetails &&
              allowEditProject(
                role,
                projectDetails.enrolmentStatus,
                projectDetails.sizing_passed
              ) && (
                <EditProjectModal
                  iconOnly
                  project={projectDetails}
                  getFilesReq={getFilesReq}
                  getUpdatedProjects={() =>
                    getProjectDetails(projectDetails.id)
                  }
                />
              )}
          </Box>
        </Box>
        <Modal
          open={openSizingTool}
          onClose={handleCloseRequest}
          title="System Sizing Tool"
          disableBackdropClick={false}
          boxSx={{
            maxWidth: 900,
            minWidth: 650,
            width: "100%",
            background: "#fff",
            borderRadius: "24px",
            boxShadow: "0 4px 32px rgba(0,0,0,0.10)",
            padding: "32px",
            "& > *:first-of-type": {
              marginBottom: "24px",
            },
            "& > *:not(:first-of-type)": {
              paddingLeft: 0,
              paddingRight: 0,
            },
          }}
        >
          {((): React.ReactElement => {
            if (sizingModalType === "form") {
              return (
                <SizingToolForm
                  sizingForm={sizingForm}
                  setSizingForm={setSizingForm}
                  handleAnalyze={handleAnalyze}
                  analyzing={analyzing}
                  setOpenSizingTool={setOpenSizingTool}
                  useForm={useForm}
                  handleSizingInput={handleSizingInput}
                  getBatteryOems={getBatteryOems}
                  getBatteryList={getBatteryList}
                />
              );
            } else {
              if (projectDetails) {
                return (
                  <SizingToolResultModal
                    type={sizingModalType}
                    sizingResult={sizingResult}
                    sizingError={sizingError}
                    accountHash={
                      validatedAccountNumber ||
                      projectDetails?.accountHash ||
                      "N/A"
                    }
                    customerName={
                      validatedCustomerName ||
                      projectDetails?.customerName ||
                      "N/A"
                    }
                    projectId={projectId || projectDetails?.id}
                    projectDetails={projectDetails}
                    sizingFormData={sizingForm}
                    onBack={() => setSizingModalType("form")}
                    onAnalyze={handleAnalyze}
                    onUpload={handleModalUpload}
                    uploading={uploading}
                    file={file}
                    setFile={setFile}
                    uploadSuccess={uploadSuccess}
                    handleFileChange={handleFileChange}
                    handleUpload={handleUpload}
                    handleCalculationFailureUpload={(() => {
                      return uploadDocumentHandler;
                    })()}
                    analyzing={analyzing}
                    hideUploadAndNextSteps={false}
                    onClose={() => {
                      setOpenSizingTool(false);
                      setSizingError(null);
                      setSizingResult(null);
                      setShowUpload(false);
                      setUploadSuccess(false);
                      setSizingModalType("form");
                    }}
                  />
                );
              }
              return <Box />;
            }
          })()}
        </Modal>
        {isProjectLoaded && (
          <BoxInfoWrapper sx={{ marginTop: "40px" }}>
            <ProjectInfoItem title={"Incentive status"}>
              <IncentiveStatusSelect
                projectId={projectDetails?.id}
                incentiveStatus={projectDetails?.incentiveStatus}
                getProjectsReq={refreshProjectDetails}
                usableCapacity={projectDetails?.usableCapacity}
                numberOfBatteries={projectDetails?.numberOfBatteries}
              />
            </ProjectInfoItem>
            <ProjectInfoItem title={"Enrollment status"}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <BadgeSelect
                  readOnly={isEnrollmentReadOnly}
                  currentSelectedItem={
                    projectDetails?.enrolmentStatus as string
                  }
                  items={enrollmentItems}
                  onSelectItem={(value) =>
                    updateEnrolmentStatus(projectDetails?.id, value)
                  }
                  badgeVariantFn={getEnrollmentBadgeVariant}
                />
              </Box>
            </ProjectInfoItem>
            {hasSizingVerdict && (
              <ProjectInfoItem title={"Sizing status"}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <StyledBadge
                    sx={{
                      display: "inline-flex",
                      textWrap: "nowrap",
                      width: "100%",
                    }}
                    {...getSizingBadgeVariant(sizingStatus)}
                  >
                    {hasSizingToolBeenPassed
                      ? "Sufficient"
                      : hasSizingToolBeenRejected
                      ? "Failed"
                      : "Pending"}
                  </StyledBadge>
                </Box>
              </ProjectInfoItem>
            )}
            {(isAdmin || isContractor) && hasSizingToolBeenPassed && (
              <ProjectInfoItem title={""}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    height: "100%",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setShowSignatureConfirmation(true)}
                    sx={{
                      padding: "6px 12px",
                      fontSize: "14px",
                      color: "#0076CE",
                      borderColor: "#0076CE",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: "rgba(0, 118, 206, 0.04)",
                        borderColor: "#0076CE",
                      },
                    }}
                  >
                    Send for signature
                  </Button>
                </Box>
              </ProjectInfoItem>
            )}
            {(projectDetails.operationalizedDate || isAdmin) && (
              <ProjectInfoItem title="Operationalized Date">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant={"body1Medium"}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "block",
                      color: theme.palette.text.primary,
                      textAlign: "center",
                      width: "100%",
                    }}
                  >
                    {projectDetails.operationalizedDate
                      ? formatOperationalizedDate(
                          projectDetails.operationalizedDate
                        )
                      : "Not set"}
                  </Typography>
                  {isAdmin && Boolean(projectDetails?.id) && (
                    <EditOperationalizedDateModal
                      projectId={projectDetails.id}
                      currentDate={projectDetails.operationalizedDate || null}
                      onSuccess={() => {
                        getProjectDetails(projectDetails.id);
                        setSuccessMessage(
                          `Operationalized date updated successfully for Project #${projectDetails.id}`
                        );
                      }}
                    />
                  )}
                </Box>
              </ProjectInfoItem>
            )}
            {isAdmin && (
              <ProjectInfoItem title={""}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    height: "100%",
                    justifyContent: "flex-end",
                    gap: "8px",
                  }}
                >
                  {!hasSizingToolBeenPassed && (
                    <Button
                      variant="outlined"
                      onClick={() => setShowPassSizingConfirmation(true)}
                      disabled={isPassingSizingTool}
                      sx={{
                        padding: "4px 8px",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#0076CE",
                        borderColor: "#0076CE",
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                        minWidth: "auto",
                        textTransform: "none",
                        height: "auto",
                        "&:hover": {
                          backgroundColor: "rgba(0, 118, 206, 0.04)",
                          borderColor: "#0076CE",
                        },
                        "&:disabled": {
                          opacity: 0.6,
                          cursor: "not-allowed",
                        },
                      }}
                    >
                      {isPassingSizingTool
                        ? "Processing..."
                        : "Pass Sizing Tool"}
                    </Button>
                  )}
                  {/* SDCP-1325: Emily needs to reject sizing, not only pass it. */}
                  {!hasSizingToolBeenRejected && (
                    <Button
                      variant="outlined"
                      onClick={() => setShowRejectSizingConfirmation(true)}
                      disabled={isRejectingSizingTool}
                      sx={{
                        padding: "4px 8px",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#B53700",
                        borderColor: "#B53700",
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                        minWidth: "auto",
                        textTransform: "none",
                        height: "auto",
                        "&:hover": {
                          backgroundColor: "rgba(181, 55, 0, 0.04)",
                          borderColor: "#B53700",
                        },
                        "&:disabled": {
                          opacity: 0.6,
                          cursor: "not-allowed",
                        },
                      }}
                    >
                      {isRejectingSizingTool
                        ? "Processing..."
                        : "Reject Sizing Tool"}
                    </Button>
                  )}
                </Box>
              </ProjectInfoItem>
            )}
          </BoxInfoWrapper>
        )}
      </Card>

      <ConfirmProjectSignatureModal
        open={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onSuccess={handleConfirm}
        projectDetails={projectDetails}
        allowEmailEdit={isAdmin}
      />

      <ConfirmationModals
        showSignatureConfirmation={showSignatureConfirmation}
        setShowSignatureConfirmation={setShowSignatureConfirmation}
        showPassSizingConfirmation={showPassSizingConfirmation}
        setShowPassSizingConfirmation={setShowPassSizingConfirmation}
        showRejectSizingConfirmation={showRejectSizingConfirmation}
        setShowRejectSizingConfirmation={setShowRejectSizingConfirmation}
        projectId={projectDetails?.id}
        onConfirmSignature={handleConfirmSignature}
        onConfirmPassSizing={handleConfirmPassSizing}
        onConfirmRejectSizing={handleConfirmRejectSizing}
        isPassingSizingTool={isPassingSizingTool}
        isRejectingSizingTool={isRejectingSizingTool}
      />

      {!!successMessage && (
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          sx={{ zIndex: 1000 }}
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSuccessSnackbar}
        >
          <Alert onClose={handleCloseSuccessSnackbar} severity="success">
            {successMessage}
          </Alert>
        </Snackbar>
      )}

      {!!errorMessage && (
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          sx={{ zIndex: 1000 }}
          open={!!errorMessage}
          autoHideDuration={6000}
          onClose={handleCloseErrorSnackbar}
        >
          <Alert onClose={handleCloseErrorSnackbar} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}

      <ConfirmationModal
        open={showTerminationConfirmation}
        onClose={handleTerminationCancel}
        onConfirm={handleTerminationConfirm}
        title="Terminate System Sizing Process"
        message="Are you sure you want to terminate the system sizing process?"
        confirmText="Yes"
        cancelText="No"
        confirmButtonColor="#DC3545"
      />

      <RejectedProjectModal
        projectId={rejectProjectId}
        getProjectsReq={refreshProjectDetails}
        onClose={() => setRejectProjectId(0)}
      />
      <CorrectionNeededModal
        projectId={correctionNeededProjectId}
        getProjectsReq={refreshProjectDetails}
        onClose={() => setCorrectionNeededProjectId(0)}
      />
    </Box>
  );
};

export default ProjectInfoCard;
